import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {Router} from '@angular/router';
import {PersonLogin, RoleName} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {PersonLoginService} from '../services/person-login-service';
import {ProductService} from '../services/product-service';
import {SelectionService} from '../services/selection-service';
import {DateUtils} from '../utils/date-utils';
import {StringUtils} from '../utils/string-utils';
import {Product} from './product';
import { ProductPricesComponent } from './product-prices.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatProgressSpinner, ProductPricesComponent, DecimalPipe, DatePipe]
})
export class ProductDetailComponent implements OnInit {
  readonly productId = input.required<string>({ alias: 'product-id' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly productService = inject(ProductService);
  private readonly router = inject(Router);
  private readonly selectionService = inject(SelectionService);


  public personLogin!: PersonLogin;
  readonly loadedProduct = signal<Product>(undefined!);
  readonly isNew = signal<boolean>(false);
  readonly productTypes = signal<SelectionItem[]>(undefined!);
  readonly commodities = signal<SelectionItem[]>(undefined!);
  readonly productLines = signal<SelectionItem[]>(undefined!);
  readonly productClassCodes = signal<SelectionItem[]>(undefined!);
  readonly productModels = signal<SelectionItem[]>(undefined!);
  readonly productLeadTimes = signal<SelectionItem[]>(undefined!);
  readonly uomCodes = signal<SelectionItem[]>(undefined!);
  public pricesVisible: boolean = false;
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>(undefined!);

  public showPrices() {
    this.pricesVisible = true;
  }

  public updateNumericUserField1(newValue: any): void {
    if (isNaN(+newValue)) {
      this.loadedProduct().numericUserField1 = 0;
    } else {
      this.loadedProduct().numericUserField1 = +newValue;
    }
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setBeginEffective(newValue: Date) {
    this.loadedProduct().beginEffectiveDate = DateUtils.formatOutput(newValue);
  }

  public setEndEffective(newValue: Date) {
    this.loadedProduct().endEffectiveDate = DateUtils.formatOutput(newValue);
  }

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    {
      const idString: string = this.productId();
      if (idString && idString.length > 0) {
        if ("new" === idString) {
          this.startNewProduct();
        } else {
          this.productService.loadProduct(idString).subscribe(result => this.loadedProduct.set(result));
        }
      } else {
        this.backToProductSearch();
      }
    }
  }

  public backToProductSearch(): void {
    this.router.navigate(['product']);
  }

  private loadSelectionLists(): void {
    this.selectionService.getProductTypes().subscribe(result => this.productTypes.set(result));
    this.selectionService.getCommodities().subscribe(result => this.commodities.set(result));
    this.selectionService.getProductLines().subscribe(result => this.productLines.set(result));
    this.selectionService.getProductClassCodes().subscribe(result => this.productClassCodes.set(result));
    this.selectionService.getProductModels().subscribe(result => this.productModels.set(result));
    this.selectionService.getProductLeadTimes().subscribe(result => this.productLeadTimes.set(result));
    this.selectionService.getUomCodes().subscribe(result => this.uomCodes.set(result));
  }

  private startNewProduct(): void {
    this.isNew.set(true);
    this.loadedProduct.set(new Product());
    this.loadedProduct().active = true;
    this.loadedProduct().numericUserField1 = 0;
    this.loadedProduct().beginEffectiveDate = new Date(2001, 0, 1);
    this.loadedProduct().endEffectiveDate = new Date(2029, 11, 31);
    this.loadedProduct().createTimestamp = new Date();
    this.loadedProduct().createUser = this.personLogin.person.userLogin;
    this.loadedProduct().updateTimestamp = new Date();
    this.loadedProduct().updateUser = this.personLogin.person.userLogin;
  }

  public saveProduct(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.loadedProduct().updateUser = this.personLogin.person.userLogin;
    this.productService.save(this.loadedProduct()).subscribe(() => {
      this.isBusy.set(false);
      this.productService.loadProduct(this.loadedProduct().productId).subscribe(result => this.loadedProduct.set(result));
      this.loadedProduct.set(undefined!);
    }, error => this.handleError(error));
  }

  private handleError(error: HttpErrorResponse) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (!this.personLoginService.hasRole(RoleName.IECIT)) {
      this.router.navigate(['main']);
    }
    this.personLogin = this.personLoginService.getCachedLogin()!;
    this.initialize();
  }

  viewSkuSlots(): void {
    this.router.navigate(['product', this.loadedProduct().productId, 'skuSlots']);
  }

}

